import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { AmazonHoverTriangleTrackerDropdownDirection, useAmazonHoverTriangleTracker } from "@faire/retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/AmazonHoverTriangle/useAmazonHoverTriangleTracker";
import { LazyDropdowns } from "@faire/retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/Dropdown/LazyDropdowns";
import { L1Link } from "@faire/retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/L1Link";
import { SaleAnnouncementTooltip } from "@faire/retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/SaleAnnouncementTooltip";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useMaybeAssignCategoryTreeExperiment } from "@faire/retailer-visitor-shared/lib/useMaybeAssignCategoryTreeExperiment";
import { useSettingDealsHub } from "@faire/retailer-visitor-shared/settings/useSettingDealsHub";
import { useSettingNextJSRscCategoryPage } from "@faire/retailer-visitor-shared/settings/useSettingNextJSRscCategoryPage";
import { useNavigationQuery } from "@faire/retailer-visitor-shared/stores/domain/useNavigationQuery";
import { QualtricsRetailerEvent } from "@faire/web--source/common/in-product-survey/qualtrics";
import { triggerQualtricsEvent } from "@faire/web--source/common/in-product-survey/qualtrics";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { useDelayedCallback } from "@faire/web--source/ui/hooks/useDelayedCallback";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackRetailersTopNavView } from "@faire/web-api--source/events/retailers/view/topNav";
import { IRecordCategoryNodesVisibilityRequest } from "@faire/web-api--source/indigofair/category/IRecordCategoryNodesVisibilityRequest";
import { ClientPlatformType } from "@faire/web-api--source/indigofair/data/ClientPlatformType";
import { INavigationLink as INavigationLinkData } from "@faire/web-api--source/indigofair/data/INavigationLink";
import { IQueryFlashSalesRequest } from "@faire/web-api--source/indigofair/data/IQueryFlashSalesRequest";
import { route as categoryC1Route } from "@faire/web-api--source/routes/category/c1CategoryUrlId";
import { observer } from "mobx-react";
import React, { Suspense, useCallback, useEffect, useMemo, useRef } from "react";
import styled, { css } from "styled-components";
var Source = IRecordCategoryNodesVisibilityRequest.Source;
var HOVER_DELAY = 250;
// left and right padding for navigation links bar
var NAVBAR_HORIZONTAL_PADDING = 24;
export var IsUsingTouchGesturesContext = /*#__PURE__*/ React.createContext(false);
/**
 * The navigation links bar that displays a three-level link hierarchy.
 *
 * L1 links appear in the horizontal navigation bars, and typically represent
 * product categories (but they can also represent non-product categories,
 * such as featured links). Clicking on L1 links opens a dropdown that then
 * shows the child links of the selected L1 link.
 *
 * The dropdown is split into a left and a right column. The left column
 * shows the child links (which can be both L2 and L3) of the selected
 * L1 link. The right column shows the child L3 links of the selected L2 link
 * in the left column.
 *
 * The dropdown is loaded lazily to avoid inflating the initial JS bundle.
 */ var NavigationLinksBarBase = function() {
    var isSSR = useIsSSR();
    var isDesktopAndAbove = useViewport().isDesktopAndAbove;
    var _useState = _sliced_to_array(useState(false), 2), isUserInteractingWithTouch = _useState[0], setIsUserInteractingWithTouch = _useState[1];
    var _useState1 = _sliced_to_array(useState(0), 2), resetHoverCounter = _useState1[0], setResetHoverCounter = _useState1[1];
    var _useState2 = _sliced_to_array(useState(false), 2), shouldShowDropdown = _useState2[0], setShouldShowDropdown = _useState2[1];
    var _useState3 = _sliced_to_array(useState(undefined), 2), selectedLinkName = _useState3[0], setSelectedLinkName = _useState3[1];
    var _useState4 = _sliced_to_array(useState(undefined), 2), lastFocusedLinkName = _useState4[0], setLastFocusedLinkName = _useState4[1];
    var mouseOverLinkName = useRef(undefined);
    var pointerEventInitiatedWithTouch = useRef(false);
    var cancelDropVisibilityTimeoutRef = useRef(undefined);
    var navBarRef = useRef(null);
    var linksRef = useRef(null);
    var _useNextRouterContext = useNextRouterContext(), nextPrefetch = _useNextRouterContext.prefetch;
    var onMouseMove = useAmazonHoverTriangleTracker(linksRef, function() {
        return setSelectedLinkName(mouseOverLinkName.current);
    }, AmazonHoverTriangleTrackerDropdownDirection.BOTTOM).onMouseMove;
    var maybeAssignCategoryTreeExperiment = useMaybeAssignCategoryTreeExperiment();
    var navigationLinkGroup = useNavigationQuery(ClientPlatformType.CLIENT_DESKTOP).navigationLinkGroup;
    var allDesktopC1Links = useMemo(function() {
        return navigationLinkGroup.flatMap(function(group) {
            return group.navigation_links;
        });
    }, [
        navigationLinkGroup
    ]);
    var visibleLinks = useMemo(function() {
        return allDesktopC1Links.filter(function(link) {
            return link.style_group !== INavigationLinkData.StyleGroup.INVISIBLE && link.style_group !== INavigationLinkData.StyleGroup.INVISIBLE_FOR_ASSIGNMENT;
        }).map(function(param) {
            var name = param.name, deep_link_path = param.deep_link_path, path_identifier = param.path_identifier, unique_identifier = param.unique_identifier, sublinks = param.sublinks, sale_source = param.sale_source;
            return {
                name: name !== null && name !== void 0 ? name : "",
                to: isUserInteractingWithTouch && sublinks.length > 0 ? undefined : deep_link_path,
                categoryPathIdentifier: path_identifier,
                uniqueIdentifier: unique_identifier,
                isWarehouseSale: sale_source === IQueryFlashSalesRequest.SearchSource.SEASONAL_SALE
            };
        });
    }, [
        allDesktopC1Links,
        isUserInteractingWithTouch
    ]);
    var categoryPathIdentifiersForAssignment = useMemo(function() {
        var invisibleForAssignmentCategoryPathIdentifiers = allDesktopC1Links.filter(function(link) {
            return link.style_group === INavigationLinkData.StyleGroup.INVISIBLE_FOR_ASSIGNMENT;
        }).map(function(navigationLink) {
            return navigationLink.path_identifier;
        });
        var visibleCategoryPathIdentifiers = visibleLinks.map(function(link) {
            return link.categoryPathIdentifier;
        });
        return _to_consumable_array(invisibleForAssignmentCategoryPathIdentifiers).concat(_to_consumable_array(visibleCategoryPathIdentifiers));
    }, [
        allDesktopC1Links,
        visibleLinks
    ]);
    useEffect(function() {
        if (isDesktopAndAbove) {
            maybeAssignCategoryTreeExperiment(function() {
                return categoryPathIdentifiersForAssignment;
            }, Source.WEB_DESKTOP_TOP_NAV);
        }
    }, [
        isDesktopAndAbove,
        categoryPathIdentifiersForAssignment,
        maybeAssignCategoryTreeExperiment
    ]);
    var delayDropdownVisibility = useDelayedCallback(function() {
        return setShouldShowDropdown(true);
    });
    var showDropDown = function(delay) {
        cancelDropVisibilityTimeoutRef.current = delayDropdownVisibility(delay);
    };
    // Needs to be wrapped in a useCallback for performance reasons
    var closeOpenDropdown = useCallback(function() {
        setSelectedLinkName(undefined);
        mouseOverLinkName.current = undefined;
    }, []);
    /**
     * Starting interaction with touch. Set flags that the interaction was initiated with touch
     */ var handleContentTouchStart = function() {
        pointerEventInitiatedWithTouch.current = true;
        setIsUserInteractingWithTouch(true);
    };
    /**
     * **`mouseenter`** will be fired no matter if pointer type is `coarse` or `fine`.
     * - If mouseenter is fired by a touch gesture, it will be fired after `touchstart`.
     *  see [reference](https://developer.mozilla.org/en-US/docs/Web/API/Touch_events/Supporting_both_TouchEvent_and_MouseEvent).
     *
     *  In the touchstart event above, set the pointerEventInitiatedWithTouch flag to true to indicated that the intertaction was
     *  started with touch.
     *
     *  Upon entering mouseEnnter, `wasJustTouched` is set to true in `touchstart`, when we get get, will know that the user is interacting
     *  with the links using touch gestures if and only if `wasJustTouched`=true
     */ var handleContentMouseEnter = function() {
        var localIsUserInteractingWithTouch = isUserInteractingWithTouch;
        if (!pointerEventInitiatedWithTouch.current && isUserInteractingWithTouch) {
            // Event wasn't initiated by touch, but isUserInteractingWithTouch is still true
            // Need to reset it to false for the downstream event handlers
            localIsUserInteractingWithTouch = false;
            setIsUserInteractingWithTouch(localIsUserInteractingWithTouch);
        }
        if (!shouldShowDropdown && !localIsUserInteractingWithTouch) {
            // Mouse entered the links bar, so set dropdown to show after a delay
            showDropDown(HOVER_DELAY);
        }
        // Reset pointerEventInitiatedWithTouch as it isn't needed for the remainder of the event chain
        pointerEventInitiatedWithTouch.current = false;
    };
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var isRSCCategoryPageEnabled = useSettingNextJSRscCategoryPage();
    /**
     * This event will be fired after content mouse enter (on both touch and mouse events)
     * If interacting through touch, the selected link will be handled in the click event
     *
     * If interacting through a mouse, these represent a mouse over the nav link
     */ var handleMouseEnterLink = function(link) {
        if (!isUserInteractingWithTouch && lastFocusedLinkName !== link.name) {
            mouseOverLinkName.current = link.name;
            // Mouse hovered the
            if (!selectedLinkName) {
                if (isLoggedInRetailer && !isRSCCategoryPageEnabled) {
                    nextPrefetch(categoryC1Route);
                }
                setSelectedLinkName(link.name);
            }
            setLastFocusedLinkName(link.name);
        }
    };
    var handleClickLink = function(link) {
        if (isUserInteractingWithTouch) {
            // This click was initiated through a touch event
            // - If user tapped the already selected link name, assume they want to toggle to close the dropdown
            // - If user tapped a different item, we assume they want to switch to that link.
            if (selectedLinkName === link.name) {
                setShouldShowDropdown(false);
                closeOpenDropdown();
            } else {
                if (isLoggedInRetailer && !isRSCCategoryPageEnabled) {
                    nextPrefetch(categoryC1Route);
                }
                mouseOverLinkName.current = link.name;
                setSelectedLinkName(link.name);
                showDropDown(0);
            }
        } else {
            // The click was initiated through the mouse, Bump the key to refresh the hover behaviour.
            setResetHoverCounter(resetHoverCounter + 1);
            closeOpenDropdown();
            // TODO: https://fairewholesale.atlassian.net/browse/FD-251821
            // clean up after Discovery sub-jobs measurement survey is complete
            if (isLoggedInRetailer) {
                triggerQualtricsEvent(QualtricsRetailerEvent.clickOnCatnav);
            }
        }
    };
    var handleDropdownClick = function() {
        setLastFocusedLinkName(undefined);
        closeOpenDropdown();
    };
    var handleContentMouseLeave = function() {
        var _cancelDropVisibilityTimeoutRef_current;
        if (!isUserInteractingWithTouch) {
            // Mouse left the links bar, so cancel the dropdown show
            setSelectedLinkName(undefined);
            setLastFocusedLinkName(undefined);
            mouseOverLinkName.current = undefined;
            setShouldShowDropdown(false);
        }
        (_cancelDropVisibilityTimeoutRef_current = cancelDropVisibilityTimeoutRef.current) === null || _cancelDropVisibilityTimeoutRef_current === void 0 ? void 0 : _cancelDropVisibilityTimeoutRef_current.cancel();
    };
    var handleNavLinksBarVisible = useDoActionWhenVisible(trackRetailersTopNavView);
    return /*#__PURE__*/ _jsx(IsUsingTouchGesturesContext.Provider, {
        value: isUserInteractingWithTouch,
        children: /*#__PURE__*/ _jsx(Wrapper, {
            ref: handleNavLinksBarVisible,
            children: isSSR ? null : /*#__PURE__*/ _jsxs(Content, {
                ref: navBarRef,
                onMouseEnter: handleContentMouseEnter,
                onFocus: handleContentMouseEnter,
                onMouseLeave: handleContentMouseLeave,
                onBlur: handleContentMouseLeave,
                onTouchStart: handleContentTouchStart,
                children: [
                    /*#__PURE__*/ _jsxs(LinksWrapper, {
                        onMouseMove: onMouseMove,
                        "data-test-id": "desktop-header-c1-categories",
                        children: [
                            visibleLinks === null || visibleLinks === void 0 ? void 0 : visibleLinks.map(function(link) {
                                return /*#__PURE__*/ _jsx(StyledLink, {
                                    link: link,
                                    selectedLinkName: selectedLinkName,
                                    onClick: handleClickLink,
                                    onMouseEnter: handleMouseEnterLink,
                                    $hasDeepLink: !!link.to,
                                    tooltip: /*#__PURE__*/ _jsx(TooltipRenderer, {
                                        categoryLink: link
                                    })
                                }, link.name);
                            }),
                            /*#__PURE__*/ _jsx(DropdownAnchor, {
                                ref: linksRef
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Suspense, {
                        fallback: null,
                        children: /*#__PURE__*/ _jsx(LazyDropdowns, {
                            selectedLinkName: shouldShowDropdown ? selectedLinkName : undefined,
                            onClick: handleDropdownClick
                        })
                    })
                ]
            }, resetHoverCounter)
        })
    });
};
export var NavigationLinksBar = observer(NavigationLinksBarBase);
var TooltipRenderer = function(param) {
    var categoryLink = param.categoryLink;
    var isSaleHubUpdate = useSettingDealsHub();
    if (categoryLink.categoryPathIdentifier === "sale" && isSaleHubUpdate) {
        return /*#__PURE__*/ _jsx(SaleAnnouncementTooltip, {});
    }
    return null;
};
var StyledLink = styled(L1Link).withConfig({
    componentId: "sc-814c48b1-0"
})([
    "padding:4px 7px;position:relative;",
    ""
], function(param) {
    var $hasDeepLink = param.$hasDeepLink;
    return $hasDeepLink ? css([
        "cursor:pointer;"
    ]) : "";
});
var Wrapper = styled.div.withConfig({
    componentId: "sc-814c48b1-1"
})([
    "display:flex;flex-direction:row;justify-content:center;background:#fff;width:100%;min-height:47px;"
]);
var Content = styled.div.withConfig({
    componentId: "sc-814c48b1-2"
})([
    "display:flex;background:",
    ";position:relative;"
], Core.surface.inverse);
var LinksWrapper = styled.div.withConfig({
    componentId: "sc-814c48b1-3"
})([
    "position:relative;display:flex;flex-direction:row;justify-content:center;flex-wrap:wrap;padding:10px ",
    "px 8px;"
], NAVBAR_HORIZONTAL_PADDING);
var DropdownAnchor = styled.div.withConfig({
    componentId: "sc-814c48b1-4"
})([
    "visibility:hidden;position:absolute;bottom:0;width:100%;"
]);
